// The Vue build version to load with the `import` command
// (runtime-only or standalone) has been set in webpack.base.conf with an alias.
import Vue from 'vue';
import VModal from 'vue-js-modal';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import App from './App';
import router from './routes';
import store from './store';
import axios from 'axios';
import globalAxios from './modules/axios';
import authAxios from './modules/auth/axios';
import optimusAxios from './modules/optimus/axios';
import dataAxios from './modules/data/axios';
import { mapGetters } from 'vuex';
import { sanitizeUrl } from "@braintree/sanitize-url";

Vue.component('v-select', vSelect);

Vue.config.productionTip = false;
Vue.use(VModal, { dynamic: true, injectModalsContainer: true });

const rootElement = document.documentElement;
// get brand id from domain
const brand = window.location.host.includes('tekcloud') && 'tektronix' || 'initialstate';

// check for search or pathname that is before hash then replace it
let newHash = window.location.pathname + window.location.search;
if (newHash && newHash !== '/' && newHash !== '#/') {
	window.history.replaceState({}, '', '/#' + newHash);
}

function applyBranding(themeURL, env) {
	const themePath = themeURL + brand;
	// create a link tag for branding
	var cssLink = document.createElement('link');
	cssLink.type = 'text/css';
	cssLink.rel = 'stylesheet';
	cssLink.href = themePath + '/styles.css';
	document.head.insertBefore(cssLink, null);

	var faviconLink = document.createElement('link');
	faviconLink.rel = 'icon';
	faviconLink.href = themePath + '/fav/favicon.ico';
	document.head.appendChild(faviconLink, null);

	var manifestLink = document.createElement('link');
	manifestLink.rel = 'manifest';
	// keep manifest as local asset, but point to images from theme.init.st
	manifestLink.href = '/static/manifests/' + env +  '/' + brand + '.json';
	document.head.appendChild(manifestLink, null);

	var browserConfigMeta = document.createElement('meta');
	browserConfigMeta.name = 'msapplication-config';
	browserConfigMeta.content = themePath + '/fav/browserconfig.xml';
	document.head.appendChild(browserConfigMeta, null);

	var appleIconLink = document.createElement('link');
	appleIconLink.sizes = "180x180";
	appleIconLink.rel = 'apple-touch-icon';
	appleIconLink.href = themePath + '/fav/apple-touch-icon-180x180.png';
	document.head.appendChild(appleIconLink, null);

	var icon16Link = document.createElement('link');
	icon16Link.sizes = "16x16";
	icon16Link.type = "image/png";
	icon16Link.rel = 'icon';
	icon16Link.href = themePath + '/fav/favicon-16x16.png';
	document.head.appendChild(icon16Link, null);

	var icon32Link = document.createElement('link');
	icon32Link.sizes = "32x32";
	icon32Link.type = "image/png";
	icon32Link.rel = 'icon';
	icon32Link.href = themePath +  '/fav/favicon-32x32.png';
	document.head.appendChild(icon32Link, null);

	var safariIconLink = document.createElement('link');
	safariIconLink.color = "#ffffff";
	safariIconLink.rel = 'mask-icon';
	safariIconLink.href = themePath +  '/fav/safari-pinned-tab.svg';
	document.head.appendChild(safariIconLink, null);
}

function addGoogleTagManager(w, d, s, l, i) {
	w[l] = w[l] || [];
	w[l].push({
		'gtm.start': new Date().getTime(),
		event: 'gtm.js'
	});
	var f = d.getElementsByTagName(s)[0],
		j = d.createElement(s),
		dl = l !== 'dataLayer' ? '&l=' + l : '';
	j.async = true;
	j.src = sanitizeUrl('https://www.googletagmanager.com/gtm.js?id=' + i + dl);
	f.parentNode.insertBefore(j, f);
};

function checkTagManagerConsent(gtmKey, authApiBase) {
	if (!gtmKey || typeof authApiBase !== 'string') {
		return;
	}
	function addSnippet() {
		addGoogleTagManager(window, document, 'script', 'dataLayer', gtmKey);
	}

	if (window.cookieconsent) {
		window.cookieconsent.initialise({
			container: document.body,
			revokable: true,
			law: {regionalLaw: false},
			location: true,
			cookie:{ domain: document.domain.replace(/.+(?=(\.\w+){2})/,'')},
			palette:{ popup: {background: "#000"},button: {background: "#f1d600"}},
			theme: "edgeless",
			position: "bottom",
			content:{
				message: "This website uses cookies to understand how product features are used, report technical errors, and evaluate the quality of our customer engagement. \nThis information is not recorded until you accept this notice, which you may later revoke by clearing your browser's cookies.",
				dismiss: "Accept",
				policy: ""
			},
			location: {
				serviceDefinitions: {
					isauth: function () {
						return {
							url: authApiBase + 'ip',
							callback: function(done, response) {
								try {
									var data = JSON.parse(response);
									if (!data || !data.country_code) {
										throw new Error('Invalid Response');
									}
									return { code:data.country_code };
								} catch (err) {
									throw new Error('Invalid Response');
								}
							}
						};
					}
				},
				services:['isauth']
			},
			onInitialise: function() {
				if(this.hasConsented()){
					addSnippet();
				}
			},
			onStatusChange: function() {
				if(this.hasConsented()){
					addSnippet();
				}
			},
			onNoCookieLaw: addSnippet,
			addSnippet: function () {
				if (!this.hasSnippet) {
					this.hasSnippet = true;
					addSnippet();
				}
			}
		});
	} else {
		addSnippet();
	}

};

globalAxios({url: '/static/config.json', method: 'GET'})
	.then((config)=> {
		const configData = config.data;
		//set baseURLs before store is created
		authAxios.defaults.baseURL = configData.AUTH_API;
		dataAxios.defaults.baseURL = configData.DATA_SERVICE_API;
		optimusAxios.defaults.baseURL = configData.OPTIMUS_API;

		/* eslint-disable no-new */
		new Vue({
			el: '#app',
			router,
			store,
			components: { App },
			template: '<App/>',
			computed: {
				...mapGetters([
					'THEME_URL',
					'ENVIRONMENT'
				]),
			},
			data() {
				return {
					pointer: ''
				};
			},
			mounted() {
				let vm = this;
				applyBranding(this.THEME_URL, this.ENVIRONMENT);
				// provide navigating animation by on first load
				rootElement.classList.remove('navigating');

				function handleTouchEvent(event) {
					vm.$el.removeEventListener('touchstart', handleTouchEvent);
					vm.$el.removeEventListener('mousemove', handleMouseEvent);
					vm.pointer = 'touch';
				}

				function handleMouseEvent(event) {
					vm.$el.removeEventListener('touchstart', handleTouchEvent);
					vm.$el.removeEventListener('mousemove', handleMouseEvent);
					vm.pointer = 'mouse';
				}

				vm.$el.addEventListener('touchstart', handleTouchEvent);
				vm.$el.addEventListener('mousemove', handleMouseEvent);

			},
			beforeCreate(){
				//store branding in state
				configData.BRAND = brand;
				this.$store.dispatch('SET_CONFIG', configData);
			},
			created() {
				this.$router.beforeEach((to, from, next) => {
					// start the navigating animation
					rootElement.classList.add('navigating');
					// continue to next page
					next();
				});
				// finish animation after moving router-view
				this.$router.afterEach((to, from) => {
					// stop the navigating animation
					rootElement.classList.remove('navigating');
				});
				//set store to strict while in development
				store.strict = config.ENVIRONMENT !== 'production';

				if (configData.GTM_KEY) {
					checkTagManagerConsent(configData.GTM_KEY, configData.AUTH_API);
				}
			}
		});

	});



// WEBPACK FOOTER //
// ./src/main.js